import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Box, Typography, Button } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress';
import { COLORS, UI } from '../config/colors';
import ChatBubble from './ChatBubble';
import ThinkingAccordion from './ThinkingAccordion';
import { ExperienceHistogram, ExperienceOptions } from '../utils/topicStrategies/experienceStrategy';
import { ExperienceHistogramReadOnly } from '../utils/topicStrategies/experience/components';
import { CompanyStageOptions } from '../utils/topicStrategies/companyStageStrategy';
import { CompanyStageConfirmationReadOnly } from '../utils/topicStrategies/companyStage/components';
import { TitleOptions, TitleConfirmationReadOnly } from '../utils/topicStrategies/title/components';
import { LocationSelection, LocationSelectionReadOnly } from '../utils/topicStrategies/location/components';
import CandidatePreview from './CandidatePreview';
import SimpleCandidateCard from './SimpleCandidateCard';
import CompanySelection from './CompanySelection';
import SkipTopicButton from './SkipTopicButton';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import { BusinessModelOptions } from '../utils/topicStrategies/businessModel';
import SkillsConfirmation, { SkillsConfirmationReadOnly } from './SkillsConfirmation';
import Footer from './Footer';
import { deserializeMessagesFromStorage, createMessageWithComponent, deserializeComponent } from '../utils/componentMapping';
import useCentralDiscoveryQueue from '../hooks/useCentralDiscoveryQueue';
import centralDiscoveryQueue from '../utils/centralDiscoveryQueue';

const ChatInterface = ({
  messages,
  isTextStreaming,
  matchResults,
  currentAIMessage,
  matchesLoading,
  handleRefreshResults,
  actionLabels,
  handleStartIntake,
  convoStarted,
  currentDiscoveryState,
  advanceToNextTopic,
  setMessages,
  fullMessages, // New prop for full message history from AppLayout
  setFullMessages, // New prop for setting full message history
  experienceHistogramData,
  levelCounts,
  locationMap,
  locationKeys,
  companyStageData,
  companyBusinessModelData,
  titleKeys,
  setTitleKeys,
  selectedSkills,
  setSelectedSkills,
  confirmedSkills,
  setConfirmedSkills,
  selectedCompanyStages,
  setSelectedCompanyStages,
  confirmedCompanyStages,
  setConfirmedCompanyStages,
  onSendText,
  onSendVoice,
  onStopVoice,
  isRecording,
  isWaitingForResponse,
  candidateViewMode = false, // New prop for candidate view mode
  currentCandidateIndex,
  setCurrentCandidateIndex,
  profiles,
  macroMappingData, // Add macroMappingData prop
  onSwitchToMessaging, // Add callback for switching to messaging tab
  isMessagingMode = false, // Add flag for messaging mode
  sessionId, // Add sessionId prop for central discovery queue
  initialRequirements = [], // Add initialRequirements prop for central discovery queue
  userId // Add userId prop for central discovery queue
}) => {
  const [activeSection, setActiveSection] = useState(null)
  const [displayedMessages, setDisplayedMessages] = useState(messages)
  const [selectedTitles, setSelectedTitles] = useState([])
  const [showContinueDiscoveryButton, setShowContinueDiscoveryButton] = useState(false)
  const [continueDiscoveryUsed, setContinueDiscoveryUsed] = useState(false)
  
  // Ref to track scroll position during session restoration
  const scrollContainerRef = useRef(null)
  const preserveScrollPositionRef = useRef(false)

  // Central Discovery Queue Integration
  const {
    queueItems,
    isLoading: queueLoading,
    error: queueError,
    getItemsByTopic,
    getNextPendingForTopic,
    isTopicFullyDiscovered,
    markAsDiscovered,
    progressPercentage,
    topicStats
  } = useCentralDiscoveryQueue(sessionId, initialRequirements);

  // Log queue status for debugging
  useEffect(() => {
    if (queueItems.length > 0) {
      console.log('🎯 CENTRAL QUEUE STATUS:', {
        totalItems: queueItems.length,
        progressPercentage,
        topicStats,
        currentTopic: currentDiscoveryState?.currentDiscoTopic,
        queueItems: queueItems.map(item => ({
          section_id: item.section_id,
          section_name: item.section_name,
          topic: item.topic,
          disco_done: item.disco_done
        }))
      });
    }
  }, [queueItems, progressPercentage, topicStats, currentDiscoveryState?.currentDiscoTopic]);

  useEffect(() => {
    console.log(`🚀 CHATINTERFACE RECEIVED: ${messages.length} messages, ${messages.filter(msg => msg.sessionRestored === true).length} readonly`);
    console.log(`🚀 RECEIVED MESSAGES:`, messages.map(m => ({id: m.id, component: m.component, sessionRestored: m.sessionRestored, _hasComponent: m._hasComponent})));
    
    // Process messages to restore components when loading from database
    const processedMessages = deserializeMessagesFromStorage(messages);
    console.log(`🚀 CHATINTERFACE PROCESSED: ${processedMessages.length} messages, ${processedMessages.filter(msg => msg.sessionRestored === true).length} readonly`);
    console.log(`🚀 PROCESSED MESSAGES:`, processedMessages.map(m => ({id: m.id, component: m.component, sessionRestored: m.sessionRestored, _hasComponent: m._hasComponent})));
    
    setDisplayedMessages(processedMessages);
  }, [messages])
  
  // Simple useEffect to detect session restoration and show Continue Discovery button
  useEffect(() => {
    console.log('🔄 SESSION RESTORATION CHECK');
    console.log('🔄 displayedMessages.length:', displayedMessages.length);
    console.log('🔄 displayedMessages with sessionRestored=true:', displayedMessages.filter(msg => msg.sessionRestored === true).length);
    console.log('🔄 currentDiscoveryState?.currentDiscoTopic:', currentDiscoveryState?.currentDiscoTopic);
    console.log('🔄 showContinueDiscoveryButton:', showContinueDiscoveryButton);
    console.log('🔄 continueDiscoveryUsed:', continueDiscoveryUsed);
    
    // Check if we have readonly components from session restoration
    const hasReadonlyComponents = displayedMessages.some(msg => msg.sessionRestored === true);
    console.log('🔄 hasReadonlyComponents:', hasReadonlyComponents);
    
    // SIMPLIFIED LOGIC: Show Continue Discovery button if we have session-restored components
    // and we haven't already used the button and we haven't already shown the button
    if (hasReadonlyComponents && !showContinueDiscoveryButton && !continueDiscoveryUsed) {
      console.log('🔄 SHOWING CONTINUE DISCOVERY BUTTON - session has restored components and button not used yet');
      setShowContinueDiscoveryButton(true);
    }
    
    // Hide the button if we no longer have readonly components (shouldn't happen but safety check)
    if (!hasReadonlyComponents && showContinueDiscoveryButton) {
      console.log('🔄 HIDING CONTINUE DISCOVERY BUTTON - no more restored components');
      setShowContinueDiscoveryButton(false);
    }
  }, [displayedMessages, showContinueDiscoveryButton, continueDiscoveryUsed])

  const toggleSection = actionKey => {
    setActiveSection(prev => (prev === actionKey ? null : actionKey))
  }

  // Function to handle Continue Discovery button click
  const handleContinueDiscovery = () => {
    console.log('🚀 CONTINUE DISCOVERY BUTTON CLICKED');
    
    // Hide the button immediately and mark as used
    setShowContinueDiscoveryButton(false);
    setContinueDiscoveryUsed(true);
    
    // Get the session restore info to determine what message to send
    const restoreInfo = window.sessionRestoreInfo;
    
    if (restoreInfo && onSendText) {
      console.log('🚀 Using session restore info to continue discovery:', restoreInfo);
      
      // Determine the appropriate message based on the last completed topic
      let continueMessage = 'continue';
      
      switch (restoreInfo.lastCompletedTopic) {
        case 'skills':
          continueMessage = 'I am done with my selections for Skills and am ready to move on to the next topic.';
          break;
        case 'title':
          continueMessage = 'I am done with my selections for Job Titles and am ready to move on to the next topic.';
          break;
        case 'location':
          continueMessage = 'I am done with my selections for Location and am ready to move on to the next topic.';
          break;
        case 'company_stage':
          continueMessage = 'I am done with my selections for Company Stage and am ready to move on to the next topic.';
          break;
        default:
          continueMessage = 'continue';
      }
      
      console.log('🚀 Sending continue message:', continueMessage);
      
      setTimeout(() => {
        onSendText(continueMessage);
      }, 200);
    } else if (onSendText) {
      console.log('🚀 No session restore info, sending generic continue');
      
      setTimeout(() => {
        onSendText('continue');
      }, 200);
    }
  };

  // Find valid AI messages, ignoring the first message
  const validAIMessages = displayedMessages
    .map((msg, index) =>
      index > 0 && 
      msg.role === 'AI' && 
      !msg.content.includes('Typing..') && 
      !msg.content.startsWith('Hey Hannah 👋') 
        ? index 
        : null
    )
    .filter(index => index !== null)

  // Find the latest valid AI message index
  const latestAIMessageRealIndex =
    validAIMessages.length > 0
      ? validAIMessages[validAIMessages.length - 1]
      : null
  
  // Check if the last message is a valid AI message
  const isLastMessageValidAI =
    displayedMessages.length > 0 &&
    displayedMessages[displayedMessages.length - 1].role === 'AI' &&
    !displayedMessages[displayedMessages.length - 1].content.includes('Typing..') &&
    !displayedMessages[displayedMessages.length - 1].content.startsWith('Hey Hannah 👋')
  
  // Check if thinking content is being shown
  const hasThinkingContent = displayedMessages.some(msg => 
    msg.role === 'AI' && msg.thinking && msg.content === 'Typing...'
  );
  
  // Check if we should disable the footer (when skills confirmation, title selection is shown or thinking is active)
  const shouldDisableFooter = (selectedSkills && selectedSkills.length > 0) || 
                              (titleKeys && titleKeys.length > 0 && currentDiscoveryState?.currentDiscoTopic === 'title') ||
                              hasThinkingContent;
  
  // Debug logging for candidate preview
  console.log('ChatInterface Debug:', {
    currentTopic: currentDiscoveryState?.currentDiscoTopic,
    candidateViewMode: candidateViewMode,
    hasProfiles: profiles && Object.keys(profiles).length > 0,
    hasMatchResults: matchResults && matchResults.length > 0,
    currentCandidateIndex: currentCandidateIndex,
    profilesCount: profiles ? Object.keys(profiles).length : 0,
    matchResultsCount: matchResults ? matchResults.length : 0
  });

  // Determine if we should show candidate view mode
  // This can happen either when candidateViewMode is true with profiles, 
  // or when we're in candidate_preview topic with matchResults
  const shouldShowCandidateView = candidateViewMode && (
    (profiles && Object.keys(profiles).length > 0) || 
    (matchResults && matchResults.length > 0)
  );

  // If in candidate view mode, show only candidates
  if (shouldShowCandidateView) {
    // Determine which data source to use
    const candidatesData = profiles && Object.keys(profiles).length > 0 ? profiles : matchResults;
    const isUsingProfiles = profiles && Object.keys(profiles).length > 0;
    
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
          flex: 1,
          overflow: 'hidden',
          bgcolor: '#FFFFFF',
          borderRadius: '0px',
          padding: '0px',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            bgcolor: '#FFFFFF',
            width: '100%',
            height: '100%',
            overflowY: 'auto',
            overflowX: 'hidden',
            paddingBottom: '120px',
            paddingTop: '20px',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#F9FAFB',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#E5E7EB',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#D1D5DB',
            }
          }}
        >
          {/* Centered content container */}
          <Box
            sx={{
              width: '100%',
              maxWidth: '800px',
              padding: '0 16px',
            }}
          >
            {/* Display feedback messages */}
            {displayedMessages.map((message, index) => {
              if (!message.content) return null;
              
              const isFirstInSequence = index === 0 || 
                displayedMessages[index - 1].role !== message.role;
              
              return (
                <React.Fragment key={index}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: message.role === 'User' ? 'flex-end' : 'flex-start',
                      mb: 2,
                      width: '100%',
                      maxWidth: '100%',
                      paddingRight: message.role === 'User' ? 8 : 0,
                    }}
                  >
                    <ChatBubble 
                      message={message.content}
                      isAI={message.role === 'AI'}
                      isFirstInSequence={isFirstInSequence}
                      key={message.id || index}
                    />
                  </Box>
                </React.Fragment>
              );
            })}

            {/* Top 5 candidates display with pagination */}
            {(() => {
              if (isUsingProfiles) {
                // Use profiles data - show 5 at a time with pagination
                const profileIds = Object.keys(profiles);
                const startIndex = Math.floor(currentCandidateIndex / 5) * 5;
                const endIndex = startIndex + 5;
                const currentPageProfileIds = profileIds.slice(startIndex, endIndex);
                const totalPages = Math.ceil(profileIds.length / 5);
                const currentPage = Math.floor(currentCandidateIndex / 5) + 1;
                
                return currentPageProfileIds.length > 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      mb: 2,
                      width: '100%',
                      maxWidth: '95%',
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: '#333',
                        }}
                      >
                        Candidates {startIndex + 1}-{Math.min(endIndex, profileIds.length)} of {profileIds.length}
                      </Typography>
                      <Typography
                        sx={{
                          color: '#6B7280',
                          fontSize: '14px',
                        }}
                      >
                        Page {currentPage} of {totalPages}
                      </Typography>
                    </Box>
                    {currentPageProfileIds.map((profileId) => {
                      const profile = profiles[profileId];
                      return profile ? (
                        <SimpleCandidateCard
                          key={profileId}
                          candidate={profile}
                          profileId={profileId}
                          macroMappingData={macroMappingData}
                          onSendMessage={onSendText}
                          onRefreshResults={handleRefreshResults}
                          setMessages={setFullMessages || setMessages}
                          messages={fullMessages || messages}
                        />
                      ) : null;
                    })}
                  </Box>
                ) : null;
              } else {
                // Use matchResults data - show 5 at a time with pagination
                const startIndex = Math.floor(currentCandidateIndex / 5) * 5;
                const endIndex = startIndex + 5;
                const currentPageCandidates = matchResults.slice(startIndex, endIndex);
                const totalPages = Math.ceil(matchResults.length / 5);
                const currentPage = Math.floor(currentCandidateIndex / 5) + 1;
                
                return currentPageCandidates.length > 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      mb: 2,
                      width: '100%',
                      maxWidth: '90%',
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: '#333',
                        }}
                      >
                        Candidates {startIndex + 1}-{Math.min(endIndex, matchResults.length)} of {matchResults.length}
                      </Typography>
                      <Typography
                        sx={{
                          color: '#6B7280',
                          fontSize: '14px',
                        }}
                      >
                        Page {currentPage} of {totalPages}
                      </Typography>
                    </Box>
                    {currentPageCandidates.map((candidate, index) => (
                      <CandidatePreview
                        key={candidate?.profileId || index}
                        candidate={candidate}
                        profileId={candidate?.profileId}
                        currentIndex={startIndex + index}
                        totalCandidates={matchResults.length}
                      />
                    ))}
                  </Box>
                ) : null;
              }
            })()}

            {/* Pagination buttons */}
            {(() => {
              const totalCount = isUsingProfiles ? Object.keys(profiles).length : matchResults.length;
              const totalPages = Math.ceil(totalCount / 5);
              const currentPage = Math.floor(currentCandidateIndex / 5) + 1;
              
              if (totalCount <= 5) return null; // Don't show pagination if 5 or fewer candidates
              
              return (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 2,
                    mb: 2,
                    width: '100%',
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => {
                      const newIndex = Math.max(0, currentCandidateIndex - 5);
                      setCurrentCandidateIndex(newIndex);
                    }}
                    disabled={currentPage === 1}
                    sx={{
                      borderColor: '#E5E7EB',
                      color: '#6B7280',
                      '&:hover': {
                        borderColor: '#3B82F6',
                        backgroundColor: '#F3F4F6',
                      },
                      '&:disabled': {
                        borderColor: '#E5E7EB',
                        color: '#D1D5DB',
                      },
                    }}
                  >
                    Previous 5
                  </Button>
                  
                  <Typography
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      color: '#6B7280',
                      fontSize: '14px',
                      minWidth: '100px',
                      justifyContent: 'center',
                    }}
                  >
                    Page {currentPage} of {totalPages}
                  </Typography>
                  
                  <Button
                    variant="outlined"
                    onClick={() => {
                      const newIndex = Math.min(totalCount - 1, currentCandidateIndex + 5);
                      setCurrentCandidateIndex(newIndex);
                    }}
                    disabled={currentPage === totalPages}
                    sx={{
                      borderColor: '#E5E7EB',
                      color: '#6B7280',
                      '&:hover': {
                        borderColor: '#3B82F6',
                        backgroundColor: '#F3F4F6',
                      },
                      '&:disabled': {
                        borderColor: '#E5E7EB',
                        color: '#D1D5DB',
                      },
                    }}
                  >
                    Next 5
                  </Button>
                </Box>
              );
            })()}

            {/* Done Calibrating Button */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 4,
                mb: 3,
                width: '100%',
              }}
            >
              <Button
                variant="contained"
                onClick={() => {
                  if (onSwitchToMessaging) {
                    onSwitchToMessaging();
                  }
                }}
                sx={{
                  bgcolor: '#4599FA',
                  color: '#FFFFFF',
                  borderRadius: '8px',
                  padding: '12px',
                  textTransform: 'none',
                  fontFamily: 'Roboto',
                  fontWeight: 400,
                  width: '100%',
                  fontSize: '14px',
                  '&:hover': {
                    bgcolor: '#3b82d6',
                  },
                }}
              >
                Done calibrating - let's draft candidate messaging
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        flex: 1,
        overflow: 'hidden', // Change to hidden to prevent scrolling on the container
        bgcolor: '#FFFFFF', // White background to match Figma design
        borderRadius: '0px',
        padding: '0px',
        position: 'relative', // Add relative positioning for footer
      }}
    >
      <Box
        ref={scrollContainerRef}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center', // Center the content horizontally
          bgcolor: '#FFFFFF', // White background to match Figma design
          width: '100%',
          height: '100%',
          overflowY: 'auto', // Enable scrolling
          overflowX: 'hidden', // Prevent horizontal scrolling
          paddingBottom: convoStarted ? '120px' : '20px', // Only add bottom padding when search is initialized
          paddingTop: '20px', // Add padding at the top to match Figma design
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#F9FAFB',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#E5E7EB',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#D1D5DB',
          }
        }}
      >
        {/* Centered content container */}
        <Box
          sx={{
            width: '100%',
            maxWidth: '800px', // Increased from 640px for wider content
            padding: '0 16px', // Increased padding for better spacing
          }}
        >
          {displayedMessages.map((message, index) => {
            console.log(`🔍 PROCESSING MESSAGE ${index}:`, {
              id: message.id,
              role: message.role,
              hasComponent: !!message.component,
              componentName: typeof message.component === 'string' ? message.component : message.component?.name,
              hasComponentProps: !!message.componentProps,
              sessionRestored: message.sessionRestored,
              contentLength: message.content?.length || 0,
              willSkipTyping: message.content === 'Typing...' && !message.thinking && currentAIMessage,
              willSkipNoContent: !message.content
            });
            
            // Only skip rendering if it's a generic "Typing..." message without thinking content and we have currentAIMessage
            if (message.content === 'Typing...' && !message.thinking && currentAIMessage) {
              console.log(`⏭️ SKIPPING MESSAGE ${index}: Typing message with currentAIMessage`);
              return null;
            }
            // Skip messages with no content UNLESS they have a component to render
            if (!message.content && !message.component) {
              console.log(`⏭️ SKIPPING MESSAGE ${index}: No content and no component`);
              return null;
            }
            
            // Determine if this is the first message in a sequence from the same sender
            const isFirstInSequence = index === 0 || 
              displayedMessages[index - 1].role !== message.role;
            
            return (
              <React.Fragment key={index}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: message.role === 'User' ? 'flex-end' : 'flex-start',
                    mb: 2,
                    width: '100%',
                    maxWidth: '100%', // Ensure the box doesn't exceed container width
                    mr: message.role === 'User' ? 0 : 0, // Remove right margin since it's handled in ChatBubble
                    paddingRight: message.role === 'User' ? 8 : 0, // Extra padding on the right for user messages
                  }}
                >
                  {/* Show thinking accordion only when there's no content yet (just "Typing...") */}
                  {message.role === 'AI' && message.thinking && message.content === 'Typing...' ? (
                    <ThinkingAccordion 
                      title="Show thinking"
                      content={message.thinking}
                      isStreaming={true}
                      responseContent={null}
                      key={message.id || index}
                    />
                  ) : (
                    <ChatBubble 
                      message={message.content}
                      isAI={message.role === 'AI'}
                      isFirstInSequence={isFirstInSequence}
                      key={message.id || index} // Add key prop to help React identify each message uniquely
                    />
                  )}
                  
                  {/* Render component if message has a component property */}
                  {message.component && message.componentProps && (
                    (() => {
                      console.log('🎯 RENDERING MESSAGE COMPONENT:', {
                        id: message.id,
                        component: message.component,
                        sessionRestored: message.sessionRestored,
                        hasComponentProps: !!message.componentProps
                      });
                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: message.role === 'User' ? 'flex-end' : 'flex-start',
                            mt: 1,
                            width: '100%',
                            maxWidth: '90%',
                          }}
                        >
                          {(() => {
                            try {
                              // Ensure we have a valid React component function
                              let ComponentToRender = message.component;
                              
                              // If it's a string, try to deserialize it to get the actual component
                              if (typeof message.component === 'string') {
                                console.log('🔧 Component is string, deserializing:', message.component);
                                ComponentToRender = deserializeComponent(message.component);
                                console.log('🔧 Deserialized component:', ComponentToRender);
                              }
                              
                              if (!ComponentToRender || typeof ComponentToRender !== 'function') {
                                console.error('❌ Invalid component:', ComponentToRender);
                                return <div style={{color: 'red'}}>Invalid component: {message.component}</div>;
                              }
                              
                              const component = React.createElement(ComponentToRender, message.componentProps);
                              console.log('✅ COMPONENT RENDERED SUCCESSFULLY:', ComponentToRender?.name || ComponentToRender);
                              return component;
                            } catch (error) {
                              console.error('❌ COMPONENT RENDER ERROR:', error);
                              console.error('❌ COMPONENT:', message.component);
                              console.error('❌ PROPS:', message.componentProps);
                              return <div style={{color: 'red'}}>Component render error: {error.message}</div>;
                            }
                          })()}
                        </Box>
                      );
                    })()
                  )}
                  
                  {/* Intake choices inline: Start Fresh | Start with JD */}
                  {message.role === 'AI' && 
                   (message.content.includes("Ready to begin?") || message.content.includes("Let's begin your intake meeting")) && 
                   (!convoStarted || (displayedMessages || []).every(m => m && m.role !== 'User')) && (
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'flex-start', 
                      alignItems: 'center',
                      gap: 1.5,
                      width: '100%', 
                      mt: 2,
                      // Align with chat bubble content
                      marginLeft: '48px'
                    }}>
                      <Button
                        variant="contained"
                        onClick={handleStartIntake}
                        sx={{
                          backgroundColor: '#FFFFFF',
                          color: '#101828',
                          fontWeight: 400,
                          fontSize: '14px',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          border: '1px solid #DCDFEA',
                          boxShadow: 'none',
                          textTransform: 'none',
                          '&:hover': {
                            backgroundColor: '#F9FAFB',
                            boxShadow: 'none',
                          }
                        }}
                      >
                        Start Fresh
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => { if (typeof window.handleStartWithJD === 'function') window.handleStartWithJD(); }}
                        sx={{
                          backgroundColor: '#FFFFFF',
                          color: '#101828',
                          fontWeight: 400,
                          fontSize: '14px',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          border: '1px solid #DCDFEA',
                          boxShadow: 'none',
                          textTransform: 'none',
                          '&:hover': {
                            backgroundColor: '#F9FAFB',
                            boxShadow: 'none',
                          }
                        }}
                      >
                        Start with JD
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => { if (typeof window.handleStartTopicBased === 'function') window.handleStartTopicBased(); }}
                        sx={{
                          borderColor: '#EAECF0',
                          color: '#101828',
                          fontWeight: 400,
                          fontSize: '14px',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          textTransform: 'none',
                          '&:hover': { borderColor: '#98A2B3', color: '#101828', backgroundColor: '#F9FAFB' }
                        }}
                      >
                        Start From Scratch
                      </Button>
                    </Box>
                  )}


                  {/* Refresh Matches button hidden as requested */}
                  {((latestAIMessageRealIndex == index) && isLastMessageValidAI && matchesLoading) ? (
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <CircularProgress size={24} sx={{ color: COLORS.primary.purple }} />
                    </Box>
                  ) : <></>}

                  {(latestAIMessageRealIndex == index) && isLastMessageValidAI &&
                    actionLabels?.length > 0 &&
                    matchResults && (
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        {actionLabels.map((label, labelIndex) => {
                          if (label.key == 'findem_search_page') {
                            return (
                              <Button
                                key={labelIndex}
                                size='small'
                                variant='contained'
                                sx={{
                                  bgcolor: activeSection === label.key
                                    ? COLORS.primary.purple
                                    : COLORS.primary.purpleLight,
                                  color: 'white',
                                  '&:hover': {
                                    bgcolor: activeSection === label.key
                                      ? COLORS.primary.purple
                                      : COLORS.primary.purpleLight,
                                    opacity: 0.9
                                  }
                                }}
                                onClick={label.clickCb}
                              >
                                {label.label}
                              </Button>
                            )
                          }
                          return (
                            <Button
                              key={labelIndex}
                              size='small'
                              variant='contained'
                              sx={{
                                bgcolor: activeSection === label.key 
                                  ? COLORS.primary.purple 
                                  : COLORS.primary.purpleLight,
                                color: 'white',
                                '&:hover': {
                                  bgcolor: activeSection === label.key
                                    ? COLORS.primary.purple
                                    : COLORS.primary.purpleLight,
                                  opacity: 0.9
                                }
                              }}
                              onClick={() => toggleSection(label.key)}
                            >
                              {label.label}
                            </Button>
                          )
                        })}
                      </Box>
                    )}

                  {(latestAIMessageRealIndex == index) && isLastMessageValidAI && activeSection && (
                    <Box
                      sx={{
                        mt: 1,
                        p: 2,
                        borderRadius: UI.borderRadius.large,
                        maxWidth: '70%',
                        bgcolor: COLORS.background.primary,
                        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)'
                      }}
                    >
                      {actionLabels
                        .find(label => label.key === activeSection)
                        ?.component()}
                    </Box>
                  )}
                </Box>
              </React.Fragment>
            );
          })}

          {/* Skills confirmation component - show when we have selected skills and are on skills topic */}
          {/* BUT only if there are no readonly skills components already in the messages */}
          {/* AND not when we have session-restored readonly components */}
          {(() => {
            const hasReadonlySkills = displayedMessages.some(msg => 
              (msg.component === 'SkillsConfirmationReadOnly' || 
               (typeof msg.component === 'function' && msg.component.name === 'SkillsConfirmationReadOnly'))
            );
            const hasSessionRestoredSkills = displayedMessages.some(msg => 
              msg.sessionRestored === true && 
              (msg.component === 'SkillsConfirmationReadOnly' || 
               (typeof msg.component === 'function' && msg.component.name === 'SkillsConfirmationReadOnly'))
            );
            const shouldShowInteractive = currentDiscoveryState && 
                                         currentDiscoveryState.currentDiscoTopic === 'skills' && 
                                         selectedSkills && 
                                         selectedSkills.length > 0 && 
                                         !hasReadonlySkills &&
                                         !hasSessionRestoredSkills; // Don't show interactive if session-restored readonly exists
            
            console.log('🔍 SKILLS COMPONENT DEBUG:', {
              currentTopic: currentDiscoveryState?.currentDiscoTopic,
              selectedSkillsLength: selectedSkills?.length || 0,
              hasReadonlySkills,
              hasSessionRestoredSkills,
              shouldShowInteractive,
              displayedMessagesLength: displayedMessages.length,
              readonlyMessagesInDisplayed: displayedMessages.filter(m => m.component === 'SkillsConfirmationReadOnly'),
              sessionRestoredMessages: displayedMessages.filter(m => m.sessionRestored === true),
              allComponentMessages: displayedMessages.filter(m => m.component)
            });
            
            return shouldShowInteractive;
          })() && (
            <>
              {/* Render the skills confirmation component directly - it includes the message */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  mb: 2,
                  width: '100%',
                }}
              >
                <ChatBubble 
                  message="Got it! Just to confirm — you're looking for candidates with the following skills, right? 👇"
                  isAI={true}
                  isFirstInSequence={true}
                />
              </Box>              
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  mb: 2,
                  width: '100%',
                  maxWidth: '90%',
                }}
              >
                <SkillsConfirmation 
                  skills={selectedSkills}
                  onConfirmed={() => {
                    console.log('Skills confirmed');
                    // Store confirmed skills and clear selected skills
                    setConfirmedSkills([...selectedSkills]);
                    setSelectedSkills([]);
                    
                    // Create skills list for the message
                    const skillsList = selectedSkills.join(', ');
                    
                    // Create user message with the read-only component using the utility
                    const userMessage = createMessageWithComponent(
                      'User',
                      `I am looking for candidates with the following skills: ${skillsList}. This requirement is complete. Let's move on to the next topic.`,
                      SkillsConfirmationReadOnly,
                      { skills: selectedSkills }
                    );
                    
                    // Update messages state with the user message
                    setMessages(prev => [...prev, userMessage]);
                    
                    // Call handleRefreshResults to update match results
                    if (handleRefreshResults) {
                      console.log('SkillsConfirmation: Calling handleRefreshResults to update match results');
                      const allMessages = [...messages, userMessage];
                      handleRefreshResults(allMessages);
                    }
                    
                    // Use onSendText to trigger the state machine for topic advancement
                    if (onSendText) {
                      // Create a message that will be recognized by isSkipRequest in topicDecisionEngine
                      const moveOnMessage = `I am done with my selections for Skills and am ready to move on to the next topic.`;
                      console.log('SkillsConfirmation: Using onSendText to trigger state machine transition');
                      
                      // Use a small delay to ensure the messages are rendered first
                      setTimeout(() => {
                        onSendText(moveOnMessage);
                      }, 200);
                    } else {
                      console.error('SkillsConfirmation: onSendText is not available, cannot trigger state machine');
                    }
                  }}
                  onAddMore={() => {
                    console.log('User wants to add more skills');
                    
                    // Clear the selected skills to hide the component
                    setSelectedSkills([]);
                    
                    // Add message asking for more skills
                    setMessages(prev => [...prev, {
                      role: 'AI',
                      content: 'Great! Please continue chatting below to add more skills to your requirements.',
                      id: `ai_skills_add_more_${Date.now()}`
                    }]);
                  }}
                />
              </Box>
            </>
          )}

          {/* Experience Histogram component - show when we have experience data and are on experience topic */}
          {/* BUT only if there are no readonly experience components already in the messages */}
          {currentDiscoveryState && 
           currentDiscoveryState.currentDiscoTopic === 'experience' && 
           experienceHistogramData && 
           !displayedMessages.some(msg => 
             (msg.component === 'ExperienceHistogramReadOnly' || 
              (typeof msg.component === 'function' && msg.component.name === 'ExperienceHistogramReadOnly')) && 
             msg.sessionRestored === true
           ) && (
            <>
              {/* Add a chat bubble message before the experience histogram */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  mb: 2,
                  width: '100%',
                }}
              >
                <ChatBubble 
                  message="Let's take a look at the ideal work experience for this role. Based on your search criteria, here's how your current employee base is distributed by years of experience. Go ahead and select a range below to refine your candidate search. 👇"
                  isAI={true}
                  isFirstInSequence={true}
                />
              </Box>
              
              {/* Render the experience histogram below the chat bubble */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  mb: 2,
                  width: '100%',
                  maxWidth: '90%',
                }}
              >
                <ExperienceHistogram
                  experienceHistogramData={experienceHistogramData}
                  levelCounts={levelCounts}
                  advanceToNextTopic={advanceToNextTopic}
                  setMessages={setMessages}
                  onSendText={onSendText}
                  messages={messages}
                  handleRefreshResults={handleRefreshResults}
                />
              </Box>
            </>
          )}

          {/* Company Stage Options component - show when we have company stage data and are on company stage topic */}
          {/* BUT only if there are no readonly company stage components already in the messages */}
          {currentDiscoveryState && 
           currentDiscoveryState.currentDiscoTopic === 'company_stage' && 
           companyStageData && 
           !displayedMessages.some(msg => 
             (msg.component === 'CompanyStageConfirmationReadOnly' || 
              (typeof msg.component === 'function' && msg.component.name === 'CompanyStageConfirmationReadOnly')) && 
             msg.sessionRestored === true
           ) && (
            <>
              {/* Render the company stage options inline with the AI message */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  mb: 2,
                  width: '100%',
                }}
              >
                <ChatBubble 
                  message="I reviewed your existing workforce, and this is the distribution across different company stages. Which company stage(s) would you like to target?"
                  isAI={true}
                  isFirstInSequence={true}
                />
                
                {/* Render the company stage options inline below the chat bubble */}
                <Box
                  sx={{
                    mt: 1,
                    width: '100%',
                    maxWidth: '90%',
                  }}
                >
                  <CompanyStageOptions
                    stageData={companyStageData}
                    advanceToNextTopic={advanceToNextTopic}
                    setMessages={setMessages}
                    onSendText={onSendText}
                    messages={messages}
                    handleRefreshResults={handleRefreshResults}
                    onStagesSelected={(stages) => {
                      console.log('Company stages selected:', stages);
                      
                      // Store confirmed company stages
                      setConfirmedCompanyStages([...stages]);
                      
                      // Create a message about the selected stages
                      const stagesList = stages.join(', ');
                      
                      // Create user message with the read-only component using the utility
                      const userMessage = createMessageWithComponent(
                        'User',
                        `I am interested in candidates from companies at the following stages: ${stagesList}. This requirement is complete. Let's move on to the next topic.`,
                        CompanyStageConfirmationReadOnly,
                        { stages: stages, stageData: companyStageData }
                      );
                      
                      // Update messages state with the user message
                      setMessages(prev => [...prev, userMessage]);
                      
                      // Call handleRefreshResults to update match results
                      if (handleRefreshResults) {
                        console.log('CompanyStageOptions: Calling handleRefreshResults to update match results');
                        const allMessages = [...messages, userMessage];
                        handleRefreshResults(allMessages);
                      }
                      
                      // Use onSendText to trigger the state machine for topic advancement
                      if (onSendText) {
                        const moveOnMessage = `I am done with my selections for Company Stage and am ready to move on to the next topic.`;
                        console.log('CompanyStageOptions: Using onSendText to trigger state machine transition');
                        
                        // Use a small delay to ensure the messages are rendered first
                        setTimeout(() => {
                          onSendText(moveOnMessage);
                        }, 200);
                      } else {
                        console.error('CompanyStageOptions: onSendText is not available, cannot trigger state machine');
                      }
                    }}
                  />
                </Box>
              </Box>
            </>
          )}

          {/* Location Selection component - show when we have location data and are on location topic */}
          {/* BUT only if there are no readonly location components already in the messages */}
          {currentDiscoveryState && 
           currentDiscoveryState.currentDiscoTopic === 'location' && 
           locationMap && locationKeys && locationKeys.length > 0 && 
           !displayedMessages.some(msg => 
             (msg.component === 'LocationSelectionReadOnly' || 
              (typeof msg.component === 'function' && msg.component.name === 'LocationSelectionReadOnly')) && 
             msg.sessionRestored === true
           ) && (
            <>
              {/* Render the location selection component directly without chat bubble */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  mb: 2,
                  width: '100%',
                  maxWidth: '90%',
                }}
              >
                <LocationSelection
                  locationMap={locationMap}
                  locationKeys={locationKeys}
                  advanceToNextTopic={advanceToNextTopic}
                  setMessages={setMessages}
                  onSendText={onSendText}
                  messages={messages}
                  handleRefreshResults={handleRefreshResults}
                />
              </Box>
            </>
          )}

          {/* Candidate Preview component - show when we have match results and are on candidate_preview topic */}
          {currentDiscoveryState && 
           currentDiscoveryState.currentDiscoTopic === 'candidate_preview' && 
           matchResults && matchResults.length > 0 && (
            <>
              {/* Add a chat bubble message before the candidate preview */}
              <Box
                sx={{
                  display:'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  mb: 2,
                  width: '100%',
                }}
              >
                <ChatBubble 
                  message="Based on your search criteria, here's a preview of one of the top candidates that matches your requirements. This gives you an idea of the quality and type of candidates we can find for you."
                  isAI={true}
                  isFirstInSequence={true}
                />
              </Box>
              
              {/* Render the candidate preview below the chat bubble */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  mb: 2,
                  width: '100%',
                  maxWidth: '90%',
                }}
              >
                <CandidatePreview
                  candidate={matchResults[currentCandidateIndex] || matchResults[0]}
                  profileId={matchResults[currentCandidateIndex]?.profileId || matchResults[0]?.profileId}
                  currentIndex={currentCandidateIndex}
                  totalCandidates={matchResults.length}
                  macroMappingData={macroMappingData}
                />
              </Box>
            </>
          )}

          {/* Skip Topic Button - only show when there's an active discovery topic */}
          {currentDiscoveryState && currentDiscoveryState.currentDiscoTopic && (
            <SkipTopicButton
              currentTopic={currentDiscoveryState.currentDiscoTopic}
              advanceToNextTopic={advanceToNextTopic}
              setMessages={setMessages}
              handleRefreshResults={handleRefreshResults}
              isThinking={isTextStreaming || displayedMessages.some(msg => msg.role === "AI" && msg.content === "Typing...")}
              onSendText={onSendText}
            />
          )}

          {/* Continue Discovery Button - show at the end when session is restored but discovery is not active */}
          {showContinueDiscoveryButton && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'flex-start', 
              width: '100%', 
              mt: 2,
              mb: 2,
              // Add left margin to align with AI messages: AI icon (20px) + gap (12px) + chat bubble padding (16px) = 48px
              marginLeft: '48px'
            }}>
              <Button
                variant="outlined"
                onClick={handleContinueDiscovery}
                sx={{
                  backgroundColor: '#FFFFFF',
                  color: '#4599FA',
                  fontWeight: 400,
                  fontSize: '14px',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid #4599FA',
                  boxShadow: 'none',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#F0F7FF',
                    borderColor: '#3b82d6',
                    color: '#3b82d6',
                    boxShadow: 'none',
                  }
                }}
              >
                Continue Discovery
              </Button>
            </Box>
          )}
        </Box>
      </Box>
      
      {/* Footer - positioned at the bottom and aligned with chat content - only show when search is initialized */}
      {convoStarted && (
        <Footer
          onSendText={onSendText}
          onSendVoice={onSendVoice}
          onStopVoice={onStopVoice}
          isRecording={isRecording}
          disabled={isWaitingForResponse || !convoStarted || shouldDisableFooter}
          leftSidebarOpen={false}
          drawerWidth={0}
          collapsedWidth={0}
          rightSidebarOpen={false}
          rightSidebarWidth={0}
          placeholder={isMessagingMode ? "Let's define candidate messaging..." : "Describe who you are looking for"}
        />
      )}
    </Box>
  )
}

export default ChatInterface;
